import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useState, useEffect, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { WorkOrderCount } from "@dmca/frontend/widgets/rfid";
import {
  Box,
  Checkbox,
  Chip,
  Container,
  FormControlLabel,
  IconButton,
  Stack,
  Typography,
  type SxProps,
} from "@mui/material";
import { type Theme } from "@mui/material/styles";
import {
  object,
  optional,
  string,
  type InferInput,
  number,
  array,
} from "valibot";
import { PageHeader } from "@dmca/frontend/components/nav";
import { Table, useTable } from "@dmca/frontend/components/table";
import ArrowBack from "@mui/icons-material/ArrowBack";
import {
  checkPermissionsInLoader,
  useAppPermissions,
  mergeSx,
  useLocalStorage,
  useSubscription,
} from "@dmca/frontend/hooks";
import type { AppRouter } from "@dmca/frontend/trpc";
import type { inferInput, TRPCOptionsProxy } from "@trpc/tanstack-react-query";
import { luxonDTValiType } from "@dmca/utils/luxon";
import { PaginationState } from "@tanstack/react-table";
import { CellFeaturesEnum } from "@dmca/models/interface/cell-location";
import { Button } from "@dmca/frontend/components/button";

type QueryInput = inferInput<
  TRPCOptionsProxy<AppRouter>["productionJob"]["getAllWithLocationPaginated"]
>;

const TableDataType = object({
  epc: string(),
  name: optional(string()),
  dueDate: optional(luxonDTValiType()),
  priority: number(),
  criticalLocations: array(string()),
  location: string(),
  locationId: string(),
});
type TableDataType = InferInput<typeof TableDataType>;

export const Route = createFileRoute("/_authed/DeviceDetails/$id")({
  component: DeviceDetails,
  loader: async ({ context, params: { id } }) => {
    await checkPermissionsInLoader("RFID::ViewRFIDOverview", context);
    const { trpcQcProxy, qc } = context;
    qc.prefetchQuery(
      trpcQcProxy.productionJob.getAllWithLocationPaginated.queryOptions({
        page: 0,
        limit: 10,
        locationId: id,
        sort: "rfid",
      })
    );
    const cells = await qc.ensureQueryData(
      trpcQcProxy.cell.getAll.queryOptions({
        byFeatureFlag: CellFeaturesEnum.RFID,
      })
    );
    const cell = cells?.find((device) => device.cellId === id);
    if (!cell) {
      throw notFound();
    }
  },
  head: () => ({
    meta: [
      {
        title: "Device Details | DNA Dashboard",
      },
    ],
  }),
});

const renderFunc =
  (colType: string, id: string, sx?: SxProps<Theme>) =>
  (val: any, row: TableDataType) => {
    return (
      <Typography
        variant="body2"
        data-datatype={colType}
        sx={mergeSx(
          {
            color:
              row.priority === 1 || row.criticalLocations.includes(id)
                ? "error.main"
                : row.priority === 2
                ? "warning.main"
                : "inherit",
          },
          sx
        )}
      >
        {val}
      </Typography>
    );
  };

function DeviceDetails() {
  const { id } = Route.useParams();
  const { trpcQcProxy: trpc, qc } = Route.useRouteContext();
  const navigate = Route.useNavigate();
  const hasPermission = useAppPermissions();

  useSubscription(
    "onProductionJobUpdate",
    () => {
      qc.invalidateQueries(
        trpc.productionJob.getAllWithLocationPaginated.pathFilter()
      );
    },
    []
  );

  useSubscription(
    "onTagLocationUpdate",
    (data) => {
      if (
        !data ||
        data.some((x) => x.oldLocationId === id || x.newLocationId === id)
      ) {
        qc.invalidateQueries(
          trpc.productionJob.getAllWithLocationPaginated.pathFilter()
        );
      }
    },
    [id]
  );

  const { data: cell } = useQuery(
    trpc.cell.getAll.queryOptions(
      {
        byFeatureFlag: CellFeaturesEnum.RFID,
      },
      {
        select: (data) => data.find((device) => device.cellId === id),
      }
    )
  );

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const search = useMemo<QueryInput>(() => {
    const search: QueryInput = {
      page: pagination.pageIndex,
      limit: pagination.pageSize,
      sort: "rfid",
    };

    return search;
  }, [pagination.pageIndex, pagination.pageSize]);

  const [type, setType] = useLocalStorage<"rfid" | "generic">(
    `smartcell-rfid-widget-display-type-${id}`,
    "rfid"
  );

  const { data: jobs, isLoading } = useQuery(
    trpc.productionJob.getAllWithLocationPaginated.queryOptions({
      ...search,
      locationId: id,
      locationType: type,
    })
  );

  /**
   * Handles the back button click event.
   */
  const handleBackClick = () => {
    navigate({
      to: "/rfid",
    });
  };

  const { getReactTable } = useTable({
    data: jobs?.data?.map<TableDataType>((x) => ({
      epc: x.id,
      name: x.name,
      dueDate: x.dueDate,
      priority: x.priority,
      criticalLocations: x.priorityOverrideLocations,
      location: x.locationName || "",
      locationId: x.locationId || "",
    })),
    isLoading,
    schema: TableDataType,
    isServerSideRendered: true,
    totalRowCount: jobs?.totalCount ?? 0,
    searchable: false,
    controlledStates: {
      pagination: [pagination, setPagination],
    },
    initialState: {
      sorting: [
        {
          id: "priority",
          desc: false,
        },
        {
          id: "dueDate",
          desc: false,
        },
      ],
    },
    cols: {
      epc: {
        name: "W/O",
      },
      name: {
        name: "Description",
      },
      dueDate: {
        name: "Due Date",
      },
      criticalLocations: {
        show: false,
      },
      allCols: {
        sortFunc: false,
        renderFunc(val, row, key) {
          return renderFunc(key, id)(val, row);
        },
      },
      priority: {
        transformFunc: (val, row) => {
          if (row.criticalLocations.includes(id)) {
            return "Critical";
          }
          return val;
        },
      },
      location: {
        name: "Current Location",
        show: type === "generic",
      },
      locationId: {
        show: false,
      },
    },
  });

  useEffect(() => {
    qc.prefetchQuery(
      trpc.productionJob.getAllWithLocationPaginated.queryOptions({
        ...search,
        locationId: id,
        page: search.page + 1,
      })
    );

    if (search.page > 0) {
      qc.prefetchQuery(
        trpc.productionJob.getAllWithLocationPaginated.queryOptions({
          ...search,
          locationId: id,
          page: search.page - 1,
        })
      );
    }
  }, [trpc.productionJob.getAllWithLocationPaginated, qc, search, id]);

  return (
    <Container maxWidth="xl">
      <PageHeader>Device Details</PageHeader>
      <Table
        getTable={getReactTable}
        toolbarContent={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              width: "100%",
            }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <IconButton
                onClick={handleBackClick}
                sx={{ alignSelf: "center" }}
              >
                <ArrowBack />
              </IconButton>
              <Typography
                variant="h5"
                sx={{ alignSelf: "center" }}
                component="span"
              >
                {cell?.locationName}
              </Typography>
              <Chip
                color="primary"
                label={
                  type === "generic" ? (
                    <strong>GENERIC TRACKING</strong>
                  ) : (
                    <strong>RFID TRACKING</strong>
                  )
                }
                sx={{ alignSelf: "center" }}
              />
            </Box>
            <FormControlLabel
              control={
                <Checkbox
                  checked={type === "generic"}
                  onChange={() =>
                    setType((old) => (old === "rfid" ? "generic" : "rfid"))
                  }
                />
              }
              label="View Generic Tracking Jobs"
              labelPlacement="start"
            />
          </Box>
        }
        toolbarOptions={{
          disableMobileToolbar: true,
        }}
      />
      <Stack direction="row" justifyContent="space-between">
        <WorkOrderCount totalCount={jobs?.totalCount ?? 0} />
        {hasPermission("WeldingParams::ViewWeldingParams") &&
          cell?.featureFlag.includes(CellFeaturesEnum.WELD) && (
            <Link to="/welding" style={{ textDecoration: "none" }}>
              <Button>OPEN WELDING PARAMETERS</Button>
            </Link>
          )}
      </Stack>
    </Container>
  );
}
